import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { capture } from "@/lib/analytics";
import { useAuth } from "@/lib/auth";
import "@/styles/pricing.css";

declare global {
  interface Window {
    promotekit_referral?: string;
  }
}

const PLAN_PRICES: Record<string, Record<string, number>> = {
  starter: { monthly: 0, annual: 0 },
  pro: { monthly: 29, annual: 23.2 },
  agent: { monthly: 79, annual: 63.2 },
};

const PLAN_DISPLAY_NAMES: Record<string, string> = {
  starter: "Starter",
  pro: "Pro",
  agent: "Agency",
};

type Interval = "monthly" | "annual";

const plans = [
  {
    key: "starter",
    name: "Starter",
    subtitle: "Get Started Free",
    monthlyPrice: "$0",
    annualPrice: "$0",
    priceSuffix: "/mo",
    description:
      "Connect your Google Ads account and start managing campaigns. No credit card required.",
    features: [
      "10 ChatGPT chats/month",
      "1 Google Ads account",
      "10 requests/month",
    ],
    cta: "Get Started Free",
    highlighted: false,
    stripeEnabled: false,
    hasToggle: false,
    externalLink: null as string | null,
  },
  {
    key: "pro",
    name: "Pro",
    subtitle: "For Business Owners",
    monthlyPrice: "$29",
    annualPrice: "$23.20",
    priceSuffix: "/mo",
    description:
      "Connect your Google Ads account and manage campaigns with full MCP and API access.",
    features: [
      "Full ChatGPT access",
      "1 Google Ads account",
      "100 requests/month",
      "Beta Features",
    ],
    cta: "Get Pro",
    highlighted: false,
    stripeEnabled: true,
    hasToggle: true,
    externalLink: null as string | null,
  },
  {
    key: "agent",
    name: "Agency",
    subtitle: "For Agencies",
    monthlyPrice: "$79",
    annualPrice: "$63.20",
    priceSuffix: "/mo",
    description:
      "Manage multiple clients with MCC access, hosted AI, and team-ready infrastructure.",
    features: [
      "Everything in Pro",
      "10 accounts included",
      "1,000 requests/month",
      "MCC multi-account management",
      "White Label Add-on",
      "API Access Add-on",
    ],
    cta: "Get Agency",
    highlighted: true,
    stripeEnabled: true,
    hasToggle: true,
    externalLink: null as string | null,
  },
  {
    key: "enterprise",
    name: "Enterprise",
    subtitle: "Custom Solutions",
    monthlyPrice: "Custom",
    annualPrice: "Custom",
    priceSuffix: "",
    description:
      "Tailored solutions for large teams with dedicated support and custom integrations.",
    features: [
      "Everything in Agency",
      "Dedicated support",
      "Custom integrations",
      "Unlimited accounts",
      "SLA & priority access",
    ],
    cta: "Contact Us",
    highlighted: false,
    stripeEnabled: false,
    hasToggle: false,
    externalLink: "https://zimamedia.com/contact",
  },
];

export default function PricingPage() {
  const [interval, setInterval] = useState<Interval>("monthly");
  const [loading, setLoading] = useState<string | null>(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleUpgrade = async (planKey: string) => {
    capture("upgrade_clicked", { plan: planKey, interval });

    const billingInterval = interval;
    const price = PLAN_PRICES[planKey]?.[billingInterval] ?? 0;
    const displayName = PLAN_DISPLAY_NAMES[planKey] ?? planKey;
    if (window.gtag && price > 0) {
      window.gtag("event", "begin_checkout", {
        currency: "USD",
        value: price,
        items: [
          {
            item_id: planKey,
            item_name: displayName,
            price: price,
            quantity: 1,
            item_category: billingInterval,
          },
        ],
      });
    }

    setLoading(planKey);
    try {
      const res = await fetch("/api/stripe/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          plan: planKey,
          interval: interval,
          promotekit_referral: window.promotekit_referral || null,
        }),
      });
      if (res.status === 401) {
        navigate("/login", {
          state: { from: "/pricing", plan: planKey, interval },
        });
        return;
      }
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        alert(data.error || "Failed to start checkout");
      }
    } catch {
      alert("Failed to start checkout. Please try again.");
    } finally {
      setLoading(null);
    }
  };

  const isCurrentPlan = (planKey: string) => {
    if (!user) return false;
    if (planKey === "starter") return user.plan === "starter";
    if (planKey === "pro") return user.plan === "pro";
    if (planKey === "agent") return user.plan === "agent";
    return false;
  };

  return (
    <div className="pricing-page">
      <nav className="pricing-nav">
        <Link to="/" className="pricing-nav-logo">
          <img
            src="/Final-white.png"
            alt="Zima Media"
            className="pricing-logo-img"
          />
        </Link>
        <Link to="/" className="pricing-nav-back">
          &larr; Back to Home
        </Link>
      </nav>

      <div className="pricing-content">
        <div className="pricing-header">
          <h1 className="gradient-text">Simple, transparent pricing</h1>
          <p>
            Scale your Google Ads management with MCP-powered AI. Pick a plan
            that fits your needs.
          </p>
        </div>

        <div className="pricing-toggle">
          <button
            className={`pricing-toggle-btn${interval === "monthly" ? " active" : ""}`}
            onClick={() => setInterval("monthly")}
          >
            Monthly
          </button>
          <button
            className={`pricing-toggle-btn${interval === "annual" ? " active" : ""}`}
            onClick={() => setInterval("annual")}
          >
            Annual
            <span className="pricing-toggle-badge">Save 20%</span>
          </button>
        </div>

        <div className="pricing-grid">
          {plans.map((plan) => {
            const price = plan.hasToggle
              ? interval === "monthly"
                ? plan.monthlyPrice
                : plan.annualPrice
              : plan.monthlyPrice;
            const current = isCurrentPlan(plan.key);
            const isLoading = loading === plan.key;

            return (
              <div
                key={plan.key}
                className={`pricing-card${plan.highlighted ? " highlighted" : ""}`}
              >
                {plan.highlighted && (
                  <span className="pricing-badge">Most Popular</span>
                )}
                <div className="pricing-plan-name">{plan.name}</div>
                {plan.subtitle && (
                  <div className="pricing-plan-subtitle">{plan.subtitle}</div>
                )}
                <div className="pricing-plan-price">
                  {price}
                  {plan.priceSuffix && (
                    <span className="pricing-plan-price-suffix">
                      {plan.priceSuffix}
                    </span>
                  )}
                </div>
                {interval === "annual" && plan.hasToggle && (
                  <div className="pricing-annual-note">Billed annually</div>
                )}
                <p className="pricing-plan-desc">{plan.description}</p>
                <ul className="pricing-features">
                  {plan.features.map((f) => (
                    <li key={f} className="pricing-feature">
                      <svg
                        viewBox="0 0 24 24"
                        width="18"
                        height="18"
                        fill="none"
                        stroke="#22c55e"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      {f}
                    </li>
                  ))}
                </ul>
                {plan.externalLink ? (
                  <a
                    href={plan.externalLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="pricing-card-btn secondary enabled"
                    onClick={() => capture("enterprise_contact_clicked")}
                  >
                    {plan.cta}
                  </a>
                ) : plan.stripeEnabled ? (
                  current ? (
                    <button
                      className={`pricing-card-btn${plan.highlighted ? " primary" : " secondary"}`}
                      disabled
                    >
                      Current Plan
                    </button>
                  ) : (
                    <button
                      className={`pricing-card-btn${plan.highlighted ? " primary" : " secondary"} enabled`}
                      onClick={() => handleUpgrade(plan.key)}
                      disabled={isLoading}
                    >
                      {isLoading ? "Redirecting..." : plan.cta}
                    </button>
                  )
                ) : plan.key === "starter" ? (
                  current ? (
                    <button className="pricing-card-btn secondary" disabled>
                      Current Plan
                    </button>
                  ) : (
                    <button
                      className="pricing-card-btn secondary enabled"
                      onClick={() => navigate("/login")}
                    >
                      {plan.cta}
                    </button>
                  )
                ) : null}
              </div>
            );
          })}
        </div>

        <div className="pricing-back-link">
          <Link to="/login">Back to Sign In</Link>
        </div>
      </div>
    </div>
  );
}
